import { api } from "@convex/_generated/api";
import { useAction } from "convex/react";
import { CheckIcon, CopyIcon, Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface InputSectionProps {
	inputText: string;
	setInputText: (text: string) => void;
	mode: "technical" | "customer" | "neutral";
	setMode: (mode: "technical" | "customer" | "neutral") => void;
	tone: "neutral" | "formal" | "casual";
	setTone: (tone: "neutral" | "formal" | "casual") => void;
	textLength: "short" | "normal" | "long";
	setTextLength: (len: "short" | "normal" | "long") => void;
	isGenerating: boolean;
	setIsGenerating: (isGenerating: boolean) => void;
	setOutputText: (text: string) => void;
	setError: (error: string | null) => void;
	onToast: (msg: { type: "success" | "error"; message: string }) => void;
}

export default function InputSection({
	inputText,
	setInputText,
	mode,
	setMode,
	tone,
	setTone,
	textLength,
	setTextLength,
	isGenerating,
	setIsGenerating,
	setOutputText,
	setError,
	onToast,
}: InputSectionProps) {
	// convex action
	const paraphrase = useAction(api.umformulieren.paraphrase);
	const [copySuccess, setCopySuccess] = useState(false);

	const handleGenerate = async () => {
		if (!inputText.trim()) {
			setError("Bitte gib einen Satz oder Absatz ein.");
			onToast({
				type: "error",
				message: "Bitte gib einen Satz oder Absatz ein.",
			});
			return;
		}

		setIsGenerating(true);
		setError(null);

		try {
			const data = await paraphrase({
				input: inputText,
				mode,
				tone,
				textLength,
			});
			setOutputText(data.output);
			onToast({
				type: "success",
				message: "Umformulierung erfolgreich erstellt.",
			});
		} catch (_err) {
			setError(
				"Bei der Umformulierung ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
			);
			onToast({
				type: "error",
				message: "Bei der Umformulierung ist ein Fehler aufgetreten.",
			});
		} finally {
			setIsGenerating(false);
		}
	};

	const handleCopy = () => {
		navigator.clipboard.writeText(inputText);
		setCopySuccess(true);
		setTimeout(() => setCopySuccess(false), 2000);
		onToast({
			type: "success",
			message: "Kopiert! Eingabetext in Zwischenablage.",
		});
	};

	return (
		<>
			<p className="mb-4 text-gray-600 text-sm dark:text-gray-300">
				Gib einen Satz oder kurzen Absatz ein und wähle aus, wie er umformuliert
				werden soll.
			</p>

			<div className="relative mb-4">
				<Textarea
					className="input-area h-40 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
					onChange={(e) => setInputText(e.target.value)}
					placeholder={
						"Beispiel: Wir haben den Server aktualisiert und Sicherheitsupdates installiert."
					}
					value={inputText}
				/>
				{inputText && (
					<Button
						className="absolute right-2 bottom-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
						onClick={handleCopy}
						size="sm"
						type="button"
						variant="ghost"
					>
						{copySuccess ? <CheckIcon size={18} /> : <CopyIcon size={18} />}
					</Button>
				)}
			</div>

			<div className="mb-6 flex flex-wrap items-end gap-3">
				<div className="min-w-[160px] flex-1">
					<Label
						className="mb-1 text-gray-700 dark:text-gray-300"
						htmlFor="mode"
					>
						Zielrichtung
					</Label>
					<Select
						onValueChange={(v: string) =>
							setMode(v as "technical" | "customer" | "neutral")
						}
						value={mode}
					>
						<SelectTrigger className="w-full border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
							<SelectValue placeholder="Kundisch" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="customer">Kundisch (verständlich)</SelectItem>
							<SelectItem value="technical">Technischer</SelectItem>
							<SelectItem value="neutral">Neutral</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<div className="min-w-[140px] flex-1">
					<Label
						className="mb-1 text-gray-700 dark:text-gray-300"
						htmlFor="tone"
					>
						Schreibstil
					</Label>
					<Select
						onValueChange={(v: string) =>
							setTone(v as "neutral" | "formal" | "casual")
						}
						value={tone}
					>
						<SelectTrigger className="w-full border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
							<SelectValue placeholder="Neutral" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="neutral">Neutral</SelectItem>
							<SelectItem value="formal">Formal</SelectItem>
							<SelectItem value="casual">Locker</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<div className="min-w-[140px] flex-1">
					<Label
						className="mb-1 text-gray-700 dark:text-gray-300"
						htmlFor="textLength"
					>
						Textlänge
					</Label>
					<Select
						onValueChange={(v: string) =>
							setTextLength(v as "short" | "normal" | "long")
						}
						value={textLength}
					>
						<SelectTrigger className="w-full border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200">
							<SelectValue placeholder="Normal" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="short">Kurz</SelectItem>
							<SelectItem value="normal">Normal</SelectItem>
							<SelectItem value="long">Lang</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<Button
					className="bg-blue-600 text-white hover:bg-blue-700 md:min-w-[180px]"
					disabled={isGenerating || !inputText.trim()}
					onClick={handleGenerate}
					type="button"
				>
					{isGenerating ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Formuliere um...
						</>
					) : (
						"Umformulieren"
					)}
				</Button>
			</div>
		</>
	);
}
